<template>
  <div>
    <div class="form-section">
      <h4 class="member-details-form-section-header">Member information</h4>
      <caption class="required-field-indicator">* Indicates required field</caption>
      <FormField :required="false" labelText="Title / Rank" id="title-rank">
        <MultiSelect
          id="title-rank"
          :maximum="1"
          :items="refData.titleRankOptions"
          :selectedItems="getCurrentTitle"
          placeholderText="Start typing your title / rank"
          @updateData="updateTitleRank"
        />
      </FormField>
    </div>

    <div class="form-section-first-name">
      <FormField labelText="First name" forId="first-name">
        <FormInput id="first-name" type="text" disabled :modelValue="memberDetails.memberInformation.firstName" />
        <template v-slot:helperText>
          <div v-if="refData.nameHelpText" v-html="refData.nameHelpText.informationText" />
        </template>
      </FormField>
    </div>

    <div class="form-section">
      <FormField labelText="Middle name" forId="middle-name" :required="false">
        <FormInput id="middle-name" type="text" disabled :modelValue="memberDetails.memberInformation.middleName" />
      </FormField>
    </div>

    <div class="form-section">
      <FormField labelText="Last name" forId="last-name">
        <FormInput id="last-name" type="text" disabled :modelValue="memberDetails.memberInformation.lastName" />
      </FormField>
    </div>

    <div class="form-section">
      <FormField
        labelText="Preferred name"
        inputId="preferred-name"
        :required="false"
        autocomplete="nickname"
        v-model.lazy="v$.memberDetails.memberInformation.preferredName.$model"
        :error="v$.memberDetails.memberInformation.preferredName.$error"
        :errorMessage="getNameErrorMessage"
        type="text"
      />
    </div>

    <div class="form-section">
      <FormField labelText="Sex" :helperText="refData.sex.helpText">
        <ul class="radio-group large">
          <li v-for="option in refData.sex.options" :key="option.returnValue">
            <input v-model="gender" type="radio" :id="option.returnValue" :value="option.returnValue" name="memberSex" disabled />
            <label :for="option.returnValue">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>

    <div class="form-section">
      <FormField labelText="Date of birth" forId="DoB">
        <FormInput id="DoB" disabled :modelValue="DoB" class="half-size" />
      </FormField>
    </div>

    <div class="form-section">
      <FormField labelText="Covered on this membership as" forId="covered-on-membership">
        <DropdownSelect
          :items="
            refData.coveredOnMembershipOptions.map((item) => {
              return { name: item.display, value: item.returnValue };
            })
          "
          v-model="coveredOnMembershipValue"
          disabled
          :initialSelect="getCoveredOnMembershipText"
        />
        <!-- <div class="dropdown-select-wrapper">
          <select id="covered-on-membership" v-model="coveredOnMembershipValue" disabled>
            <option v-for="option in refData.coveredOnMembershipOptions" :key="option.returnValue" :value="option.returnValue">{{ option.display }}</option>
          </select>
          <label for="covered-on-membership" class="dropdown-face">{{ getCoveredOnMembershipText }}</label>
        </div> -->
      </FormField>
    </div>

    <div class="form-section">
      <FormField labelText="Residential address" forId="residential-address-input">
        <AddressForm
          id="residential-address-input"
          :disabled="!isPolicyHolderDetails || !isPolicyHolderOrFullAccess"
          @updateAddressDetail="updateAddressDetail"
          :residentialState="residentialAddress.state"
          :preFilledDetails="residentialAddress"
        />
        <template v-slot:helperText>
          <div v-if="isFullAccess && !isPolicyHolderDetails && refData.residentialAddressHelpText && refData.residentialAddressFullAccessHelpText" v-html="fullAccessResidentialAddressHelpText" />
          <div v-else-if="refData.residentialAddressHelpText" v-html="refData.residentialAddressHelpText.informationText" />
        </template>
        <Alert v-if="residentialAddressUpdated && multiplePolicyHolderMemberships" alertType="info" class="inline-message">
          <div>
            <p>Updates to residential address will reflect across all policies where this person is the policy holder, and may result in changes to premium.</p>
          </div>
        </Alert>
      </FormField>
    </div>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import { useVuelidate } from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import { CONTACT_US } from '@/router/routeNames';
import { AUSTRALIAN_STATES } from '@/constants';
import { UpdateMemberDetailsStepNames, AccessLevel } from '@/enums';
import MultiSelect from '@general/js/components/multiSelect.vue';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { NameErrorMessage } from '@general/js/enums/ErrorMessageEnum';
import { stringWithInvalidSpace, reverseFormatFormDate, getCurrentDate } from '@general/js/utils';
import AddressForm from '@general/js/components/AddressForm.vue';
import { Alert } from '@/components';
import { RelationshipValue, PolicyStatus } from '@general/js/enums';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import MemberDetailsServices from '@/services/MemberDetails';
import MemberDetailsMixin from './MemberDetailsMixin';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: UpdateMemberDetailsStepNames.step1,
  mixins: [MemberDetailsMixin],
  data() {
    return {
      coveredOnMembershipValue: '',
      stateOptions: AUSTRALIAN_STATES,
      residentialAddress: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        postcode: '',
        state: '',
        suburb: '',
      },
      addressFormInvalid: null,
      AccessLevel,
      Alert,
    };
  },

  props: {
    initialData: {
      type: Object,
    },
  },

  validations: {
    memberDetails: {
      memberInformation: {
        preferredName: {
          JOIN_NOW_NAME_VALIDATOR,
          stringWithInvalidSpace,
          maxLength: maxLength(160),
        },
      },
    },
    addressFormInvalid: {
      isValid(bool) {
        return bool === false;
      },
    },
  },

  watch: {
    residentialAddress: {
      handler(newObj, oldObj) {
        if(this.memberDetails){
          const newMemberDetails = { ...this.memberDetails };
          newMemberDetails.memberInformation.residentialAddress = { ...oldObj, ...newObj };
          this.updateMemberDetails(newMemberDetails);
        }
      },
      deep: true,
    },

    coveredOnMembershipValue(newVal) {
      const newState = { ...this.memberDetails };
      newState.memberInformation.coveredOnMembership = this.refData.coveredOnMembershipOptions.find((obj) => {
        return obj?.returnValue === newVal;
      })?.display;
      this.updateMemberDetails(newState);
    },
  },

  components: {
    MultiSelect,
    AddressForm,
    DropdownSelect,
  },

  computed: {
    getNameErrorMessage() {
      const preferredName = this.v$.memberDetails.memberInformation.preferredName;
      if (preferredName.$error && !preferredName.maxLength) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (preferredName.$error && !preferredName.JOIN_NOW_NAME_VALIDATOR) {
        return NameErrorMessage.IS_INVALID;
      }

      return '';
    },

    fullAccessResidentialAddressHelpText() {
      const informationText = this.refData?.residentialAddressHelpText?.informationText || '';
      const fullAccessInformationText = this.refData?.residentialAddressFullAccessHelpText.informationText || '';
      return `${informationText}<br/>${fullAccessInformationText}`;
    },

    DoB() {
      return reverseFormatFormDate(this.memberDetails?.memberInformation?.dateOfBirth);
    },

    gender() {
      return this.refData.sex.options.find((option) => {
        return option?.display === this.memberDetails?.memberInformation?.sex;
      })?.returnValue;
    },

    isPolicyHolderOrFullAccess() {
      return [AccessLevel.FULL_ACCESS, AccessLevel.POLICY_HOLDER_ACCESS].includes(this.currentMembership.accessLevel.toLowerCase());
    },

    isFullAccess() {
      return [AccessLevel.FULL_ACCESS].includes(this.currentMembership.accessLevel.toLowerCase());
    },

    isPolicyHolderDetails() {
      return (this.memberDetails.memberInformation.coveredOnMembership === RelationshipValue.POLICY_HOLDER);
    },
    residentialAddressUpdated() {
      const initialData = this.initialData?.memberInformation ?? {};
      // Check if any field in the residential address has changed
      return !isEqual(initialData.residentialAddress, this.memberDetails.memberInformation.residentialAddress);
    }
  },

  created() {
    this.CONTACT_US = CONTACT_US;
  },

  beforeMount() {
    this.residentialAddress = this.memberDetails.memberInformation.residentialAddress;
    return this.refData.coveredOnMembershipOptions.find((obj) => {
      return obj?.returnValue === this.memberDetails.memberInformation.coveredOnMembershipAsCode;
    })?.returnValue;
  },

  async mounted() {
    // Fetch active or suspended other member policies
    const memberPolicies = await this.fetchMemberPolicies();
    this.updateMemberPolicies(memberPolicies);

    // Only watch the state value change
    this.$watch('residentialAddress.state', function (newState, oldState) {
      const newMemberDetails = { ...this.memberDetails };
      const newResidentialAddress = { ...newMemberDetails.memberInformation.residentialAddress };
      if (newState !== oldState) {
        // Update the modified date with "YYYY-MM-DD" format
        newResidentialAddress.stateLastModified = getCurrentDate();
        newResidentialAddress.state = newState;
      }
      newMemberDetails.memberInformation.residentialAddress = { ...newResidentialAddress };
      this.updateMemberDetails(newMemberDetails);
    });
  },
  methods: {
    updateAddressDetail(addressDetail) {
      this.residentialAddress = { ...this.residentialAddress, ...addressDetail.address };
      this.addressFormInvalid = addressDetail.invalid;
    },
    updateTitleRank(data) {
      const newState = { ...this.memberDetails };
      newState.memberInformation.title = data[0]?.returnValue ?? '';
      this.updateMemberDetails(newState);
    },

    /**
   * Fetches all the policies on the accessing person. 
   * It is used to check if a person has multiple policies in CRM. 
   * The requesting person needs to be on the requesting policy.
   * @returns {object} array of memberships
   */
    async fetchMemberPolicies() {
      const memberPolicies = await MemberDetailsServices.getMemberPolicies(this.currentMembership.memberNumber, this.memberDetailsPersonCode);
      // Filter active and suspended policies only
      const filteredPolicies = memberPolicies.memberships.filter(
        (membership) => {return membership.policyStatus === PolicyStatus.ACTIVE || membership.policyStatus === PolicyStatus.SUSPENDED;},
      );
      return filteredPolicies;
    },
  },
};
</script>

<style lang="scss">
.half-size {
  max-width: calc(50% - #{spacer(0.75)});

  @include mobile {
    max-width: 100%;
  }
}

.form-section-first-name {
  margin-bottom: 0;
}
</style>
